import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { iv } from './invoiceTheme';
import InvoiceTaxpayerLookupPanel from './InvoiceTaxpayerLookupPanel';
import { branchLabel } from '../../utils/invoiceSchema';
import {
  DEFAULT_IDENTITY_SETTINGS,
  fetchIdentitySettings,
  saveIdentitySettingsApi,
  runIdentityBackfillApi,
  subscribeIdentityPool,
  subscribeRiskReviews,
  countIdentityPool,
  searchIdentityPool,
  KOLAYSOFT_POOL_BUCKET,
  maskTaxIdClient,
  maskIbanClient,
  manualPatchIdentityApi
} from '../../utils/identityClient';
import { toast } from 'react-toastify';
import InvoiceAmlPanel from './InvoiceAmlPanel';
import {
  RiskKpiStrip,
  RiskPager,
  riskShellSx,
  riskTableCellSx,
  riskTableHeadCellSx,
  riskTableWrapSx,
  riskTabsSx,
  usePagedRows
} from './riskControlShared';

const TAB = {
  ALERTS: 0,
  SOURCES: 1,
  IDENTITY: 2,
  RISK: 3,
  /** Hızlı Sorgu eskiden ayrı bir menü kalemiydi; iki kat sekmeyi bitirmek için buraya alındı. */
  LOOKUP: 4,
  /** GİB e-Fatura mükellef sorgusu (Kolaysoft getGibUser) */
  TAXPAYER: 5
};

/** Kaynak sütunu — teknik kaynak kodunu personelin anlayacağı dile çevirir. */
const identitySourceLabel = (row) => {
  const bucket = row?.sube === KOLAYSOFT_POOL_BUCKET ? 'Kolaysoft' : branchLabel(row?.sube) || row?.sube || '';
  const sources = Array.isArray(row?.sources) ? row.sources : [];
  const fromBank = sources.some((x) => String(x?.sourceType || '').match(/garanti|vakif|bank/i));
  const fromKolaysoft = Boolean(row?.kolaysoft) || sources.some((x) => x?.provider === 'kolaysoft');
  const kind = fromBank && fromKolaysoft ? 'Banka + Kolaysoft' : fromBank ? 'Banka' : fromKolaysoft ? 'Kolaysoft faturası' : sources.length ? 'Diğer' : '—';
  return row?.sube === KOLAYSOFT_POOL_BUCKET ? kind : `${kind} · ${bucket}`;
};

const formatMoneyTr = (v) =>
  Number(v || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDayTr = (day) => (day ? String(day).slice(0, 10).split('-').reverse().join('.') : '—');

const formatTs = (value) => {
  if (!value) return '—';
  const d = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('tr-TR')} ${d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`;
};

const levelChip = (level) => {
  if (level === 'high') return <Chip size="small" color="error" label="Yüksek" sx={{ fontWeight: 800, height: 22 }} />;
  if (level === 'review') return <Chip size="small" color="warning" label="İnceleme" sx={{ fontWeight: 800, height: 22 }} />;
  if (level === 'low') return <Chip size="small" color="info" label="Düşük" sx={{ fontWeight: 700, height: 22 }} />;
  return <Chip size="small" color="success" label="Normal" sx={{ fontWeight: 700, height: 22 }} />;
};

const RISK_SIGNAL_LABELS = {
  KYC_MISSING: 'Kimlik (TCKN/VKN) eksik',
  REPEATED_ROUND_AMOUNTS: 'Tekrarlayan yuvarlak tutarlar',
  HIGH_AMOUNT: 'Yüksek tutar',
  HIGH_FREQUENCY: 'Yüksek frekans (24s)',
  UNUSUAL_TRANSACTION_FREQUENCY: 'Olağandışı frekans',
  SPLIT_TRANSFER: 'Parçalı transfer şüphesi',
  IDENTITY_IBAN_NAME_CONFLICT: 'IBAN–isim çakışması',
  IDENTITY_TAXID_NAME_CONFLICT: 'Vergi no–isim çakışması',
  TAX_ID_CHANGED: 'Vergi kimliği değişti',
  MULTI_IDENTITY_IBAN: 'Aynı IBAN birden fazla kimlik'
};

const resolveRiskDisplayName = (row, identities = []) => {
  const direct = String(row?.subjectName || '').trim();
  if (direct) return direct;
  if (row?.identityId) {
    const hit = identities.find((i) => i.id === row.identityId);
    const fromPool = String(hit?.customerName || hit?.normalizedName || '').trim();
    if (fromPool) return fromPool;
  }
  const desc = String(row?.description || '').trim();
  if (desc) return desc.length > 40 ? `${desc.slice(0, 40)}…` : desc;
  return '';
};

const getActivePersonel = () => {
  try {
    return JSON.parse(localStorage.getItem('aktifPersonel') || 'null');
  } catch {
    return null;
  }
};

const summarizeBackfill = (report) => {
  const branches = report?.branches || {};
  const keys = Object.keys(branches);
  if (!keys.length) return 'Backfill tamam (şube sonucu yok)';
  const parts = keys.map((k) => {
    const b = branches[k] || {};
    return `${k}: müşteri ${b.musteriler || 0}, hareket ${b.movements || 0}, fatura ${b.invoices || 0}${
      b.error ? ` (hata)` : ''
    }`;
  });
  return `Backfill tamam — ${parts.join(' · ')}`;
};

/**
 * Faturalar > Risk Kontrolü
 * Üstte tek sekme satırı; içerik tek ekrana sığar; listeler sayfalanır.
 */
function InvoiceIdentityRiskPanel({ sube, invoiceSettings = null }) {
  const personel = getActivePersonel();
  const canOperate = Boolean(personel?.id);
  const [mainTab, setMainTab] = useState(TAB.ALERTS);
  const [identities, setIdentities] = useState([]);
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [busy, setBusy] = useState('');
  const [amlBusy, setAmlBusy] = useState('');
  const [onlyConflicts, setOnlyConflicts] = useState(false);
  const [poolCount, setPoolCount] = useState(null);
  const [identitySearch, setIdentitySearch] = useState('');
  const [searchRows, setSearchRows] = useState(null);
  const [searching, setSearching] = useState(false);
  const [amlKpis, setAmlKpis] = useState({
    today: 0,
    possible: 0,
    strong: 0,
    lastSync: '—',
    mode: 'monitor'
  });
  const [syncToken, setSyncToken] = useState(0);
  const [backfillToken, setBackfillToken] = useState(0);

  useEffect(() => {
    if (!sube) return undefined;
    setLoading(true);
    const u1 = subscribeIdentityPool(
      sube,
      (rows) => {
        setIdentities(rows);
        setLoading(false);
      },
      () => setLoading(false)
    );
    const u2 = subscribeRiskReviews(sube, setRisks, () => {});
    return () => {
      u1?.();
      u2?.();
    };
  }, [sube]);

  // Liste en son kayıtlarla sınırlı; gerçek havuz büyüklüğü ayrıca sayılır.
  useEffect(() => {
    let alive = true;
    countIdentityPool()
      .then((r) => alive && setPoolCount(r))
      .catch(() => alive && setPoolCount(null));
    return () => {
      alive = false;
    };
  }, [sube]);

  useEffect(() => {
    const text = identitySearch.trim();
    if (!text) {
      setSearchRows(null);
      setSearching(false);
      return undefined;
    }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      searchIdentityPool(text)
        .then((rows) => alive && setSearchRows(rows))
        .catch(() => alive && setSearchRows([]))
        .finally(() => alive && setSearching(false));
    }, 400);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [identitySearch]);

  const identityKpis = useMemo(() => {
    const conflicts = identities.filter(
      (i) => (i.conflicts || []).length > 0 || i.verificationLevel === 'conflict'
    );
    const missingTax = identities.filter((i) => !i.tckn && !i.vkn);
    const high = risks.filter((r) => r.level === 'high');
    const review = risks.filter((r) => r.level === 'review' || r.level === 'low');
    return {
      identities: poolCount?.total ?? identities.length,
      conflicts: conflicts.length,
      missing: missingTax.length,
      riskOpen: high.length + review.length,
      high: high.length
    };
  }, [identities, risks, poolCount]);

  const filteredIdentities = useMemo(() => {
    const base = searchRows ?? identities;
    if (!onlyConflicts) return base;
    return base.filter(
      (i) => (i.conflicts || []).length > 0 || i.verificationLevel === 'conflict'
    );
  }, [identities, searchRows, onlyConflicts]);

  const identityPager = usePagedRows(filteredIdentities, undefined, onlyConflicts ? 'conflicts' : 'all');
  const riskPager = usePagedRows(risks);

  const handleIdentityBackfill = async () => {
    setBusy('backfill');
    try {
      const r = await runIdentityBackfillApi({ sube, limitMovements: 250 });
      toast.success(summarizeBackfill(r));
    } catch (err) {
      toast.error(err?.message || 'Backfill başarısız');
    } finally {
      setBusy('');
    }
  };

  const handleClearConflict = async () => {
    if (!selected?.id || !canOperate) return;
    setBusy('patch');
    try {
      await manualPatchIdentityApi({ sube, identityId: selected.id, clearConflict: true });
      toast.success('Çakışma işaretleri temizlendi');
      setSelected(null);
    } catch (err) {
      toast.error(err?.message || 'İşlem başarısız');
    } finally {
      setBusy('');
    }
  };

  const onAmlKpis = useCallback((k) => setAmlKpis(k), []);
  const onAmlBusy = useCallback((b) => setAmlBusy(b), []);

  const kpiItems = [
    {
      label: 'MASAK inceleme',
      value: amlKpis.possible || 0,
      color: '#d97706',
      tab: TAB.ALERTS,
      hint: Number(amlKpis.possible) ? 'Uyarı sekmesine git' : 'Uyarı yok (normal)'
    },
    {
      label: 'MASAK kritik',
      value: amlKpis.strong || 0,
      color: '#dc2626',
      tab: TAB.ALERTS,
      hint: Number(amlKpis.strong) ? 'Uyarı sekmesine git' : 'Kritik yok (normal)'
    },
    {
      label: 'Kimlik kayıt',
      value: identityKpis.identities,
      color: '#0f172a',
      tab: TAB.IDENTITY,
      filter: null,
      hint: poolCount
        ? `Banka ${(poolCount.total - (poolCount.perBucket?.[KOLAYSOFT_POOL_BUCKET] || 0)).toLocaleString('tr-TR')} · Kolaysoft ${(poolCount.perBucket?.[KOLAYSOFT_POOL_BUCKET] || 0).toLocaleString('tr-TR')}`
        : 'Tüm kimlik havuzu'
    },
    {
      label: 'Kimlik çakışma',
      value: identityKpis.conflicts,
      color: '#b45309',
      tab: TAB.IDENTITY,
      filter: 'conflicts',
      hint: Number(identityKpis.conflicts)
        ? `Sadece ${identityKpis.conflicts} çakışma`
        : 'Çakışma yok'
    },
    {
      label: 'İşlem riski',
      value: identityKpis.riskOpen,
      color: '#d97706',
      tab: TAB.RISK,
      hint: Number(identityKpis.riskOpen) ? 'Risk listesine git' : 'Açık risk yok'
    },
    {
      label: 'Liste sync',
      value: amlKpis.lastSync || '—',
      color: '#64748b',
      tab: TAB.SOURCES,
      small: true,
      hint: 'Resmî listeler sekmesi'
    }
  ];

  const amlSection =
    mainTab === TAB.ALERTS
      ? 'alerts'
      : mainTab === TAB.SOURCES
        ? 'sources'
        : mainTab === TAB.LOOKUP
          ? 'lookup'
          : null;
  const taxpayerSection = mainTab === TAB.TAXPAYER;

  const anyBusy = Boolean(busy || amlBusy);

  return (
    <Box sx={riskShellSx}>
      {/* Başlık + aksiyonlar */}
      <Stack
        className="iv-risk-head"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ flexShrink: 0, minHeight: 36 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: iv.text, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
            Risk Kontrolü
          </Typography>
          <Typography sx={{ fontSize: 12, color: iv.textMuted, fontWeight: 600 }}>
            {amlKpis.mode === 'enforcement'
              ? 'Uygulama modu — güçlü eşleşmede gönderim etkilenebilir'
              : 'İzleme modu — fatura gönderimi engellenmez'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
          {(mainTab === TAB.ALERTS || mainTab === TAB.SOURCES) && (
            <>
              <Button
                size="small"
                variant="contained"
                disabled={anyBusy}
                onClick={() => setSyncToken((t) => t + 1)}
                sx={{ textTransform: 'none', fontWeight: 800, fontSize: 12.5 }}
              >
                {amlBusy === 'sync' ? 'Güncelleniyor…' : 'Listeleri güncelle'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={anyBusy}
                onClick={() => {
                  setBackfillToken((t) => t + 1);
                  setMainTab(TAB.ALERTS);
                }}
                sx={{ textTransform: 'none', fontWeight: 800, fontSize: 12.5 }}
              >
                {amlBusy === 'backfill' ? 'Taranıyor…' : 'Geçmiş tara'}
              </Button>
            </>
          )}
          {mainTab === TAB.IDENTITY && (
            <>
              <Button
                size="small"
                variant={onlyConflicts ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => setOnlyConflicts((v) => !v)}
                sx={{ textTransform: 'none', fontWeight: 800, fontSize: 12.5 }}
              >
                {onlyConflicts ? 'Tümü' : 'Çakışmalar'}
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={anyBusy}
                onClick={handleIdentityBackfill}
                sx={{ textTransform: 'none', fontWeight: 800, fontSize: 12.5 }}
              >
                {busy === 'backfill' ? 'Backfill…' : 'Backfill'}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      <RiskKpiStrip
        items={kpiItems}
        activeTab={mainTab}
        activeFilter={mainTab === TAB.IDENTITY && onlyConflicts ? 'conflicts' : null}
        onSelect={(tab, opts = {}) => {
          setMainTab(tab);
          if (tab === TAB.IDENTITY) {
            setOnlyConflicts(opts.filter === 'conflicts');
          } else {
            setOnlyConflicts(false);
          }
        }}
      />
      
      <Tabs
        value={mainTab}
        onChange={(_, v) => {
          setMainTab(v);
          if (v !== TAB.IDENTITY) setOnlyConflicts(false);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={riskTabsSx}
      >
        <Tab label="MASAK Uyarıları" />
        <Tab label="Resmî Listeler" />
        <Tab label="Kimlik Havuzu" />
        <Tab label="İşlem Riski" />
        <Tab label="Hızlı Sorgu" />
        <Tab label="Mükellef Sorgulama" />
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* AML paneli her zaman mount — KPI sync için; görünmeyen section gizli */}
        <Box
          sx={{
            display: amlSection ? 'flex' : 'none',
            flex: 1,
            minHeight: 0,
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <InvoiceAmlPanel
            sube={sube}
            section={amlSection || 'alerts'}
            onKpis={onAmlKpis}
            onBusyChange={onAmlBusy}
            syncToken={syncToken}
            backfillToken={backfillToken}
          />
        </Box>

        {taxpayerSection ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            <InvoiceTaxpayerLookupPanel sube={sube} invoiceSettings={invoiceSettings} />
          </Box>
        ) : null}

        {mainTab === TAB.IDENTITY && (
          <Box sx={riskTableWrapSx}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.25, py: 0.65, borderBottom: `1px solid ${iv.border}`, flexShrink: 0 }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Typography sx={{ fontSize: 13, fontWeight: 800 }}>Kimlik havuzu</Typography>
                <TextField
                  size="small"
                  placeholder="Ad soyad veya TCKN/VKN ara"
                  value={identitySearch}
                  onChange={(e) => setIdentitySearch(e.target.value)}
                  sx={{ width: 260, '& .MuiInputBase-root': { fontSize: 12.5, bgcolor: '#fff', height: 30 } }}
                />
                {searching ? <CircularProgress size={16} /> : null}
                {searchRows && !searching ? (
                  <Typography sx={{ fontSize: 11.5, color: iv.textMuted, fontWeight: 700 }}>
                    {searchRows.length} sonuç
                  </Typography>
                ) : null}
              </Stack>
              <Typography sx={{ fontSize: 11.5, color: iv.textMuted, fontWeight: 600 }}>
                {searchRows
                  ? 'Tüm havuzda arama (şubeler + Kolaysoft)'
                  : 'Son görülenler · IBAN / TCKN bankadan ve Kolaysoft faturalarından'}
              </Typography>
            </Stack>
            {loading ? (
              <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ flex: 1, minHeight: 0 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {['Müşteri', 'TCKN/VKN', 'IBAN', 'Kaynak', 'Güven', 'Durum'].map((h) => (
                          <TableCell key={h} sx={riskTableHeadCellSx}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {identityPager.slice.map((row) => {
                        const lastIban =
                          Array.isArray(row.ibans) && row.ibans.length ? row.ibans[row.ibans.length - 1] : '';
                        const conflicted =
                          (row.conflicts || []).length > 0 || row.verificationLevel === 'conflict';
                        return (
                          <TableRow
                            key={row.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setSelected(row)}
                          >
                            <TableCell sx={{ ...riskTableCellSx, fontWeight: 700 }}>
                              {row.customerName || row.normalizedName || '—'}
                            </TableCell>
                            <TableCell sx={riskTableCellSx}>{maskTaxIdClient(row.tckn || row.vkn)}</TableCell>
                            <TableCell sx={riskTableCellSx}>
                              {(row.ibans || []).length
                                ? `${(row.ibans || []).length} · ${maskIbanClient(lastIban)}`
                                : '—'}
                            </TableCell>
                            <TableCell sx={riskTableCellSx}>{identitySourceLabel(row)}</TableCell>
                            <TableCell sx={riskTableCellSx}>{row.confidence ?? '—'}</TableCell>
                            <TableCell sx={riskTableCellSx}>
                              {conflicted ? (
                                <Chip size="small" color="warning" label="Kontrol" sx={{ height: 22 }} />
                              ) : row.tckn || row.vkn ? (
                                <Chip size="small" color="success" label="Tamam" sx={{ height: 22 }} />
                              ) : (
                                <Chip size="small" label="Eksik" sx={{ height: 22 }} />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {!identityPager.total && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ ...riskTableCellSx, py: 2.5 }}>
                            <Alert severity="info" sx={{ py: 0.3 }}>
                              {searchRows
                                ? 'Aramaya uyan kimlik bulunamadı. İsimde tam kelime aranır (ör. “ayşe”, “yılmaz”).'
                                : <>Henüz kimlik kaydı yok. <strong>Backfill</strong> çalıştırın veya Canlı Banka yenileyin.</>}
                            </Alert>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <RiskPager
                  page={identityPager.page}
                  pageSize={identityPager.pageSize}
                  total={identityPager.total}
                  onChange={identityPager.setPage}
                />
              </>
            )}
          </Box>
        )}

        {mainTab === TAB.RISK && (
          <Box sx={riskTableWrapSx}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 1.25, py: 0.65, borderBottom: `1px solid ${iv.border}`, flexShrink: 0 }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 800 }}>İşlem riski</Typography>
              <Typography sx={{ fontSize: 11.5, color: iv.textMuted, fontWeight: 600 }}>
                Satıra tıklayın · İzleme — fatura gönderimi engellenmez
              </Typography>
            </Stack>
            <TableContainer sx={{ flex: 1, minHeight: 0 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Kişi / İşlem', 'Skor', 'Seviye', 'Sinyaller', 'Tutar', 'Tarih'].map((h) => (
                      <TableCell key={h} sx={riskTableHeadCellSx}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {riskPager.slice.map((row) => {
                    const displayName = resolveRiskDisplayName(row, identities);
                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedRisk(row)}
                      >
                        <TableCell sx={{ ...riskTableCellSx, fontWeight: 700, maxWidth: 220 }}>
                          {displayName || 'İsim yok'}
                          <Typography sx={{ fontSize: 10.5, color: iv.textMuted, fontWeight: 600 }}>
                            {row.bank ? `${row.bank} · ` : ''}
                            {row.bankMovementId
                              ? `Hareket: ${String(row.bankMovementId).slice(0, 18)}${
                                  String(row.bankMovementId).length > 18 ? '…' : ''
                                }`
                              : 'Hareket ID yok'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={riskTableCellSx}>{row.score}</TableCell>
                        <TableCell sx={riskTableCellSx}>{levelChip(row.level)}</TableCell>
                        <TableCell sx={riskTableCellSx}>
                          {(row.signals || []).map((s) => s.code).join(', ') || '—'}
                        </TableCell>
                        <TableCell sx={riskTableCellSx}>
                          {Number(row.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell sx={riskTableCellSx}>{formatTs(row.createdAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!riskPager.total && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ ...riskTableCellSx, py: 3, textAlign: 'center', color: iv.textMuted }}>
                        Risk kaydı yok.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <RiskPager
              page={riskPager.page}
              pageSize={riskPager.pageSize}
              total={riskPager.total}
              onChange={riskPager.setPage}
            />
          </Box>
        )}
      </Box>

      <Dialog open={Boolean(selectedRisk)} onClose={() => setSelectedRisk(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>İşlem riski detayı</DialogTitle>
        <DialogContent dividers>
          {selectedRisk && (
            <Stack spacing={1.1}>
              <Typography sx={{ fontSize: 13.5 }}>
                <strong>Kişi:</strong>{' '}
                {resolveRiskDisplayName(selectedRisk, identities) || '— (bankada isim yok)'}
              </Typography>
              <Typography sx={{ fontSize: 13.5 }}>
                <strong>Tutar:</strong>{' '}
                {Number(selectedRisk.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
              </Typography>
              <Typography sx={{ fontSize: 13.5 }}>
                <strong>Skor / Seviye:</strong> {selectedRisk.score} · {selectedRisk.level || '—'}
              </Typography>
              <Typography sx={{ fontSize: 13.5 }}>
                <strong>IBAN:</strong> {selectedRisk.ibanMasked || '—'}
              </Typography>
              <Typography sx={{ fontSize: 13.5 }}>
                <strong>Banka:</strong> {selectedRisk.bank || '—'}
              </Typography>
              <Typography sx={{ fontSize: 13.5, wordBreak: 'break-all' }}>
                <strong>Banka hareket ID:</strong> {selectedRisk.bankMovementId || selectedRisk.id || '—'}
              </Typography>
              {selectedRisk.identityId ? (
                <Typography sx={{ fontSize: 13.5, wordBreak: 'break-all' }}>
                  <strong>Kimlik havuzu ID:</strong> {selectedRisk.identityId}
                </Typography>
              ) : null}
              {selectedRisk.description ? (
                <Typography sx={{ fontSize: 13.5 }}>
                  <strong>Açıklama:</strong> {selectedRisk.description}
                </Typography>
              ) : null}
              <Typography sx={{ fontSize: 13.5 }}>
                <strong>Tarih:</strong> {formatTs(selectedRisk.createdAt)}
              </Typography>
              <Typography sx={{ fontWeight: 800, mt: 0.5 }}>Sinyaller</Typography>
              {(selectedRisk.signals || []).length ? (
                (selectedRisk.signals || []).map((s, i) => (
                  <Alert key={`${s.code}-${i}`} severity="info" sx={{ py: 0.3 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                      {RISK_SIGNAL_LABELS[s.code] || s.code}
                      {s.weight != null ? ` · +${s.weight}` : ''}
                    </Typography>
                    {s.detail ? (
                      <Typography sx={{ fontSize: 12.5, color: iv.textMuted }}>{s.detail}</Typography>
                    ) : null}
                  </Alert>
                ))
              ) : (
                <Typography sx={{ fontSize: 13, color: iv.textMuted }}>Sinyal yok</Typography>
              )}
              <Alert severity="warning" sx={{ py: 0.35 }}>
                İzleme kaydı — fatura / EDM gönderimi engellenmez. Canlı Banka’da bu hareket ID’sini arayarak
                ilgili işlemi bulabilirsiniz.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRisk(null)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Kimlik detayı</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={1}>
              <Typography>
                <strong>Ad Soyad:</strong> {selected.customerName || '—'}
              </Typography>
              <Typography>
                <strong>TCKN/VKN:</strong> {maskTaxIdClient(selected.tckn || selected.vkn)}
              </Typography>
              <Typography>
                <strong>Müşteri:</strong> {selected.customerId || '—'}
              </Typography>
              <Typography>
                <strong>IBANlar:</strong> {(selected.ibans || []).map(maskIbanClient).join(', ') || '—'}
              </Typography>
              <Typography>
                <strong>İlk / Son:</strong> {formatTs(selected.firstSeenAt)} — {formatTs(selected.lastSeenAt)}
              </Typography>
              <Typography>
                <strong>Güven:</strong> {selected.confidence} / {selected.verificationLevel}
              </Typography>
              <Typography>
                <strong>Kaynak:</strong> {identitySourceLabel(selected)}
              </Typography>
              {selected.kolaysoft ? (
                <Box sx={{ mt: 0.5, p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: `1px solid ${iv.border}` }}>
                  <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Kolaysoft faturaları</Typography>
                  <Typography sx={{ fontSize: 13.5 }}>
                    {selected.kolaysoft.invoiceCount} fatura · {formatMoneyTr(selected.kolaysoft.invoiceTotal)} TL ·{' '}
                    {formatDayTr(selected.kolaysoft.firstInvoiceDate)} – {formatDayTr(selected.kolaysoft.lastInvoiceDate)}
                  </Typography>
                  <Typography sx={{ fontSize: 13.5 }}>
                    e-Arşiv {selected.kolaysoft.earsivCount || 0} · e-Fatura {selected.kolaysoft.efaturaCount || 0} · son fatura{' '}
                    {selected.kolaysoft.lastInvoiceNumber}
                  </Typography>
                  {selected.kolaysoft.isEfaturaMukellef ? (
                    <Chip
                      size="small"
                      label={`e-Fatura mükellefi${selected.kolaysoft.efaturaAlias ? ` · ${selected.kolaysoft.efaturaAlias}` : ''}`}
                      sx={{ mt: 0.75, fontWeight: 800, bgcolor: '#dbeafe', color: '#1d4ed8', maxWidth: '100%' }}
                    />
                  ) : null}
                  {(selected.kolaysoft.nameVariants || []).length > 1 ? (
                    <Typography sx={{ fontSize: 12.5, color: iv.textMuted, mt: 0.75 }}>
                      Faturalardaki yazılışlar:{' '}
                      {(selected.kolaysoft.nameVariants || []).map((v) => `${v.name} (${v.count})`).join(' · ')}
                    </Typography>
                  ) : null}
                </Box>
              ) : null}
              {(selected.conflicts || []).length > 0 && (
                <>
                  <Typography sx={{ fontWeight: 800, color: '#b45309' }}>Çakışmalar</Typography>
                  {(selected.conflicts || []).map((c, i) => (
                    <Alert key={i} severity="warning">
                      {c.type}: {c.message}
                    </Alert>
                  ))}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {canOperate && (selected?.conflicts || []).length > 0 && (
            <Button onClick={handleClearConflict} disabled={busy === 'patch'}>
              Çakışmayı çöz
            </Button>
          )}
          <Button onClick={() => setSelected(null)}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Ayarlar: kimlik/risk bloğu */
export function InvoiceIdentitySettingsSection({ canEdit, sube }) {
  const [form, setForm] = useState({ ...DEFAULT_IDENTITY_SETTINGS });
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  useEffect(() => {
    fetchIdentitySettings()
      .then((r) => {
        if (r?.settings) setForm({ ...DEFAULT_IDENTITY_SETTINGS, ...r.settings });
      })
      .catch(() => {});
  }, []);

  const setBool = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.checked }));
  const setNum = (key) => (e) => setForm((p) => ({ ...p, [key]: Number(e.target.value) }));

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const r = await saveIdentitySettingsApi(form);
      setForm({ ...DEFAULT_IDENTITY_SETTINGS, ...(r.settings || form) });
      toast.success('Kimlik / risk ayarları kaydedildi');
    } catch (err) {
      toast.error(err?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleBackfill = async () => {
    if (!canEdit) return;
    setBackfilling(true);
    try {
      const r = await runIdentityBackfillApi({ sube: sube || null, limitMovements: 250 });
      toast.success(summarizeBackfill(r));
    } catch (err) {
      toast.error(err?.message || 'Backfill başarısız');
    } finally {
      setBackfilling(false);
    }
  };

  const fieldSx = {
    '& .MuiInputBase-input': { fontSize: 14, py: 0.9, fontWeight: 600 },
    '& .MuiInputLabel-root': { fontSize: 13, fontWeight: 600 },
    '& .MuiInputBase-root': { minHeight: 40 }
  };

  const switchSx = {
    ml: 0,
    mr: 1.1,
    my: 0,
    '& .MuiFormControlLabel-label': { fontSize: 13.5, fontWeight: 650, color: '#334155' }
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid #e2e8f0',
        p: 1.5,
        bgcolor: '#fff',
        height: 'fit-content',
        alignSelf: 'stretch',
        minWidth: 0,
        maxWidth: '100%',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 800,
          color: '#475569',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          mb: 0.5
        }}
      >
        Kimlik Havuzu & Risk
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#64748b', mb: 1.25 }}>
        IBAN/TCKN öğrenilir · izleme modu (fatura gönderimi engellenmez)
      </Typography>

      <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mb: 1.25, minWidth: 0 }}>
        <FormControlLabel
          control={
            <Switch
              checked={form.identityPoolEnabled !== false}
              onChange={setBool('identityPoolEnabled')}
              disabled={!canEdit}
            />
          }
          label="Kimlik"
          sx={switchSx}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.autoLearnBankIdentity !== false}
              onChange={setBool('autoLearnBankIdentity')}
              disabled={!canEdit}
            />
          }
          label="Öğrenme"
          sx={switchSx}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.transactionRiskEnabled !== false}
              onChange={setBool('transactionRiskEnabled')}
              disabled={!canEdit}
            />
          }
          label="Risk"
          sx={switchSx}
        />
        <FormControlLabel
          control={
            <Switch
              checked={form.splitDetectionEnabled !== false}
              onChange={setBool('splitDetectionEnabled')}
              disabled={!canEdit}
            />
          }
          label="Parçalı"
          sx={switchSx}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, minmax(0, 1fr))'
          },
          gap: 1.1,
          mb: 1.25,
          minWidth: 0
        }}
      >
        <TextField
          size="small"
          label="Yüksek tutar (TL)"
          type="number"
          fullWidth
          value={form.highAmountThreshold ?? ''}
          onChange={setNum('highAmountThreshold')}
          disabled={!canEdit}
          sx={fieldSx}
        />
        <TextField
          size="small"
          label="24s frekans"
          type="number"
          fullWidth
          value={form.frequency24hCount ?? ''}
          onChange={setNum('frequency24hCount')}
          disabled={!canEdit}
          sx={fieldSx}
        />
        <TextField
          size="small"
          label="İnceleme eşiği"
          type="number"
          fullWidth
          value={form.riskScoreReviewMax ?? ''}
          onChange={setNum('riskScoreReviewMax')}
          disabled={!canEdit}
          sx={fieldSx}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={!canEdit || saving}
          sx={{ textTransform: 'none', fontWeight: 800, fontSize: 13.5, minHeight: 40, px: 2 }}
        >
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleBackfill}
          disabled={!canEdit || backfilling}
          sx={{ textTransform: 'none', fontWeight: 800, fontSize: 13.5, minHeight: 40, px: 2 }}
        >
          {backfilling ? 'Backfill…' : 'Backfill'}
        </Button>
      </Stack>
    </Box>
  );
}

export default InvoiceIdentityRiskPanel;
